// src/components/settings/catalog/CatalogItemsTab.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2 (+ Stage 4).
//
// The Items tab: folder column + list + item editor. Wide screens: folders on the left, the editor
// slides in from the right. Phone: the folders fold into a "Folders" button over the list, and the
// editor opens under the list. "+ New item" while a folder is open puts the new item in it.
// After a save the row changes in place straight away (the server's list catches up a moment
// later); the folder counts refresh a couple of seconds after.
//
// Stage 4: ticking rows (or "all matching") opens the bulk bar; each bulk change is a server job
// (startCatalogBulk → runCatalogJob, CatalogJobRunner) that a reopened page can finish; favorites
// (a star per row, "Favorites" in the folder column); "Export selected"; "Move folder…".
// The selection is cleared whenever what the list shows changes (search, type, folder), so a
// change never reaches items the user can't see ticked.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, FolderInput } from 'lucide-react';
import { UNGROUPED, folderTree, folderPath, countText } from '../../../utils/catalogUi';
import { useCatalogSearch, useFolderCounts } from './useCatalogSearch';
import {
    listenFolders, saveCatalogFolder, setCatalogItemArchived, setCatalogItemFavorite, startCatalogBulk, recallJob, forgetJob, friendlyError,
} from './catalogApi';
import CatalogFolderTree from './CatalogFolderTree';
import CatalogItemList from './CatalogItemList';
import CatalogItemEditor from './CatalogItemEditor';
import CatalogFolderPicker, { folderAndBelow, TOP_LEVEL } from './CatalogFolderPicker';
import { BulkBar, PriceDialog, FolderActionDialog, ArchiveDialog } from './CatalogBulkActions';
import { useCatalogJob, JobDialog, UnfinishedJobBanner } from './CatalogJobRunner';
import ExportDialog from './CatalogExport';
import { BTN_GHOST, BTN_PRIMARY, BTN_LINK, INPUT, ErrorLine, Notice, DiscardDialog, Dialog } from './catalogUiParts';

// Wide (≥ 768px, Tailwind's md): editor as a side panel; narrower: under the list. The editor is
// mounted in ONE place only, so it never loads twice or keeps two copies of a draft.
function useIsWide() {
    const q = '(min-width: 768px)';
    const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : true);
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const m = window.matchMedia(q);
        const on = () => setWide(m.matches);
        if (m.addEventListener) m.addEventListener('change', on); else m.addListener(on);
        return () => { if (m.removeEventListener) m.removeEventListener('change', on); else m.removeListener(on); };
    }, []);
    return wide;
}

const paramsOf = (sel) => (sel.kind === 'folder' ? { folderId: sel.id }
    : sel.kind === 'ungrouped' ? { folderId: UNGROUPED }
        : sel.kind === 'archived' ? { archived: true }
            : sel.kind === 'favorites' ? { favoritesOnly: true } : {});

export default function CatalogItemsTab({ orgId, suppliers, showTax, salesTaxCodes, newSignal }) {
    const [selection, setSelection] = useState({ kind: 'all' });
    const [query, setQuery] = useState('');
    const [type, setType] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());
    const [folders, setFolders] = useState(null);
    const [foldersError, setFoldersError] = useState('');
    const [editorTarget, setEditorTarget] = useState(null); // null | { mode: 'new', folderId } | { mode: 'edit', id }
    const [notice, setNotice] = useState(null);
    const [pageError, setPageError] = useState('');
    const [rename, setRename] = useState(null); // null | { name, saving, error }
    const [moveFolder, setMoveFolder] = useState(null); // null | { target, saving, error }
    const [foldersOpenOnPhone, setFoldersOpenOnPhone] = useState(false);
    const [pendingOpen, setPendingOpen] = useState(undefined); // a target waiting on "discard?"
    const [ticked, setTicked] = useState(() => new Set());
    const [allMatching, setAllMatching] = useState(false);
    const [bulkDialog, setBulkDialog] = useState(null); // null | 'price' | 'moveToFolder' | 'addToFolder' | 'archive'
    const [exportSel, setExportSel] = useState(null); // null | { selection, label }
    const [unfinished, setUnfinished] = useState(() => recallJob(orgId));
    const dirtyRef = useRef(false);
    const editorBoxRef = useRef(null);
    const isWide = useIsWide();

    useEffect(() => listenFolders(orgId, (list) => { setFolders(list); setFoldersError(''); }, (err) => {
        console.error('Catalog: folders failed', err);
        setFoldersError("Couldn't load your folders. Refresh the page to try again.");
    }), [orgId]);

    const tree = useMemo(() => folderTree(folders || []), [folders]);
    const params = { query, type, ...paramsOf(selection) };
    const search = useCatalogSearch(params);

    // If the open folder goes away (renamed elsewhere is fine; archived/removed is not), go back to All.
    useEffect(() => {
        if (folders && selection.kind === 'folder' && !tree.byId.has(selection.id)) setSelection({ kind: 'all' });
    }, [folders, selection, tree]);

    // Counts for what the folder column shows: the fixed rows, top folders, and open folders' children.
    const countKeys = useMemo(() => {
        const keys = [{ key: 'all', input: {} }, { key: 'favorites', input: { favoritesOnly: true } }, { key: 'ungrouped', input: { folderId: UNGROUPED } }, { key: 'archived', input: { archived: true } }];
        const walk = (parentId) => {
            for (const f of tree.children(parentId)) {
                keys.push({ key: `f:${f.id}`, input: { folderId: f.id } });
                if (expanded.has(f.id)) walk(f.id);
            }
        };
        walk(null);
        return keys;
    }, [tree, expanded]);
    const { counts, refresh: refreshCounts } = useFolderCounts(countKeys);

    // ── selection (Stage 4) ──
    const clearSelection = useCallback(() => { setTicked(new Set()); setAllMatching(false); }, []);
    useEffect(() => { clearSelection(); }, [search.filterKey, clearSelection]);
    const shownIds = search.items.map((x) => x.id);
    const onTick = (id) => {
        if (allMatching) { setAllMatching(false); setTicked(new Set(shownIds.filter((x) => x !== id))); return; }
        setTicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };
    const onTickShown = (on) => { setAllMatching(false); setTicked(on ? new Set(shownIds) : new Set()); };
    const tickedRows = search.items.filter((r) => ticked.has(r.id));
    const selCount = allMatching ? search.count : tickedRows.length;
    const selExact = allMatching ? search.countIsExact === true : true;
    const anySelected = allMatching || tickedRows.length > 0;
    const barLabel = allMatching
        ? (typeof search.count === 'number' ? `All ${countText(search.count, selExact)} matching selected` : 'All matching selected')
        : `${tickedRows.length.toLocaleString('en-US')} selected`;
    const selectionForJob = () => (allMatching
        ? { filter: search.filter }
        : { items: tickedRows.map((r) => ({ id: r.id, price: r.price, cost: r.cost })) });

    // ── bulk change jobs ──
    const runner = useCatalogJob({
        orgId,
        onFinished: useCallback((result) => {
            if (result) {
                clearSelection();
                const c = result.counts || {};
                setNotice({ text: `Done: ${(c.changed || 0).toLocaleString('en-US')} changed${c.skipped ? `, ${c.skipped.toLocaleString('en-US')} left alone` : ''}.` });
            }
            setUnfinished(recallJob(orgId));
            refreshCounts();
            setTimeout(() => search.retry(), 2500); // the list reads search rows the server updates a moment after each change
        }, [orgId, clearSelection, refreshCounts, search]),
    });
    const startBulk = async (input) => {
        const res = await startCatalogBulk({ ...input, selection: selectionForJob() }); // errors go back to the dialog
        setBulkDialog(null);
        setUnfinished(null);
        runner.run({ jobId: res.jobId, action: input.action, total: res.total, processed: 0 });
    };
    const jobActive = !!runner.state;

    // ── favorites (Stage 4) ──
    const toggleFavorite = async (row) => {
        const next = !row.favorite;
        setPageError('');
        search.patchItem({ id: row.id, favorite: next });
        try {
            await setCatalogItemFavorite(row.id, next);
            if (!next && selection.kind === 'favorites') search.removeItem(row.id);
            refreshCounts();
        } catch (err) {
            search.patchItem({ id: row.id, favorite: row.favorite === true });
            setPageError(friendlyError(err, "Couldn't change the favorite. Try again."));
        }
    };

    // ── opening the editor (asks first if there are unsaved changes) ──
    const openNow = useCallback((target) => {
        setEditorTarget(target);
        dirtyRef.current = false;
        if (target) setTimeout(() => { if (editorBoxRef.current && !window.matchMedia('(min-width: 768px)').matches) editorBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
    }, []);
    const requestOpen = useCallback((target) => {
        if (editorTarget && dirtyRef.current) setPendingOpen(target);
        else openNow(target);
    }, [editorTarget, openNow]);

    const lastSignal = useRef(newSignal);
    useEffect(() => {
        if (newSignal === lastSignal.current) return;
        lastSignal.current = newSignal;
        setNotice(null);
        requestOpen({ mode: 'new', folderId: selection.kind === 'folder' ? selection.id : null, key: newSignal });
    }, [newSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const newItemHere = () => requestOpen({ mode: 'new', folderId: selection.kind === 'folder' ? selection.id : null, key: Date.now() });

    // Escape closes the editor (asking first if needed) — not while a dialog is open over it.
    useEffect(() => {
        if (!editorTarget) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !pendingOpen && !bulkDialog && !exportSel && !moveFolder && !jobActive) requestOpen(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [editorTarget, pendingOpen, requestOpen, bulkDialog, exportSel, moveFolder, jobActive]);

    const selectFolder = (sel) => {
        setSelection(sel);
        setRename(null);
        setFoldersOpenOnPhone(false);
        if (sel.kind === 'folder') {
            // Opening a folder also opens its parents in the tree, so it's visible.
            setExpanded((prev) => {
                const next = new Set(prev);
                let cur = tree.byId.get(sel.id);
                while (cur && cur.parentId && !next.has(cur.parentId)) { next.add(cur.parentId); cur = tree.byId.get(cur.parentId); }
                return next;
            });
        }
    };
    const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    const createFolder = async (parentId, name) => {
        try {
            await saveCatalogFolder({ name, parentId });
            if (parentId) setExpanded((prev) => new Set(prev).add(parentId));
            setNotice({ text: `Folder “${name}” created.` });
            refreshCounts();
            return null;
        } catch (err) {
            return friendlyError(err, "Couldn't create the folder. Try again.");
        }
    };

    const openFolder = selection.kind === 'folder' ? tree.byId.get(selection.id) : null;
    const saveRename = async () => {
        const name = rename.name.trim();
        if (!name) { setRename((r) => ({ ...r, error: 'Please give the folder a name.' })); return; }
        if (name === openFolder.name) { setRename(null); return; }
        setRename((r) => ({ ...r, saving: true, error: '' }));
        try {
            await saveCatalogFolder({ folderId: openFolder.id, name });
            setRename(null);
            setNotice({ text: `Folder renamed to “${name}”.` });
        } catch (err) {
            setRename((r) => ({ ...r, saving: false, error: friendlyError(err, "Couldn't rename the folder. Try again.") }));
        }
    };

    // ── Move folder… (Stage 4; the server checks loops and the depth cap, and re-files every item below) ──
    const hereId = openFolder ? (openFolder.parentId && tree.byId.has(openFolder.parentId) ? openFolder.parentId : TOP_LEVEL) : null;
    const saveMoveFolder = async () => {
        const target = moveFolder.target;
        if (!target || target === hereId) { setMoveFolder(null); return; }
        setMoveFolder((m) => ({ ...m, saving: true, error: '' }));
        try {
            await saveCatalogFolder({ folderId: openFolder.id, parentId: target === TOP_LEVEL ? null : target });
            const where = target === TOP_LEVEL ? 'to the top level' : `into “${folderPath(tree.byId, target)}”`;
            const items = counts[`f:${openFolder.id}`];
            // Search is re-filed by the server a moment after the move — say so only when there are items to re-file.
            const catching = items === undefined || items === null || items > 0;
            setNotice({ text: `Moved “${openFolder.name}” ${where}.${catching ? ' Search is catching up — its items may take a few seconds to show in their new place.' : ''}` });
            if (target !== TOP_LEVEL) setExpanded((prev) => new Set(prev).add(target));
            setMoveFolder(null);
            refreshCounts();
            setTimeout(() => search.retry(), 3000);
        } catch (err) {
            setMoveFolder((m) => ({ ...m, saving: false, error: friendlyError(err, "Couldn't move the folder. Try again.") }));
        }
    };

    // ── after the editor ──
    const onSaved = (row, { created }) => {
        if (created) search.addItem(row); else search.patchItem(row);
        setNotice({ text: created ? `Added “${row.name}”.` : `Saved “${row.name}”.` });
        openNow(null);
        refreshCounts();
    };
    const undoArchive = async (id, name, archivedNow) => {
        setNotice((n) => ({ ...n, busy: true }));
        try {
            await setCatalogItemArchived(id, !archivedNow);
            setNotice({ text: archivedNow ? `Restored “${name}”.` : `Archived “${name}” again.` });
            search.retry();
            refreshCounts();
        } catch (err) {
            setNotice({ text: friendlyError(err, "Couldn't undo that. Try again.") });
        }
    };
    const onArchived = (id, archivedNow, name) => {
        search.removeItem(id);
        openNow(null);
        refreshCounts();
        setNotice({
            text: archivedNow ? `Archived “${name}”. You'll find it under Archived.` : `Restored “${name}”.`,
            action: { label: 'Undo', run: () => undoArchive(id, name, archivedNow) },
        });
    };
    const onClose = ({ force, notice: text } = {}) => {
        if (force) { openNow(null); if (text) setNotice({ text }); return; }
        requestOpen(null);
    };

    const folderNames = useCallback((ids) => (Array.isArray(ids) ? ids.map((id) => (tree.byId.get(id) || {}).name).filter(Boolean).join(', ') : ''), [tree]);
    const heading = selection.kind === 'all' ? 'All items' : selection.kind === 'favorites' ? 'Favorites' : selection.kind === 'ungrouped' ? 'Ungrouped'
        : selection.kind === 'archived' ? 'Archived' : (openFolder ? folderPath(tree.byId, openFolder.id) : 'Folder');

    const editorOpen = !!editorTarget;
    const editor = editorOpen && (
        <CatalogItemEditor
            key={editorTarget.mode === 'new' ? `new-${editorTarget.key}` : editorTarget.id}
            orgId={orgId} target={editorTarget} tree={tree} suppliers={suppliers || []} showTax={showTax} salesTaxCodes={salesTaxCodes}
            onClose={onClose} onSaved={onSaved} onArchived={onArchived} onDirtyChange={(d) => { dirtyRef.current = d; }} />
    );

    const bulkBar = anySelected && (
        <BulkBar label={barLabel} archivedView={selection.kind === 'archived'} busy={jobActive}
            onMove={() => setBulkDialog('moveToFolder')} onAdd={() => setBulkDialog('addToFolder')} onPrices={() => setBulkDialog('price')}
            onExport={() => setExportSel({
                selection: allMatching ? { kind: 'filter', filter: search.filter } : { kind: 'items', items: tickedRows.map((r) => r.id) },
                label: allMatching ? (typeof search.count === 'number' ? `${countText(search.count, selExact)} items` : 'all matching items') : `${tickedRows.length.toLocaleString('en-US')} item${tickedRows.length === 1 ? '' : 's'}`,
            })}
            onArchive={() => setBulkDialog('archive')} onClear={clearSelection} />
    );

    return (
        <div className="space-y-4">
            {!jobActive && (
                <UnfinishedJobBanner job={unfinished} onFinish={() => runner.run(unfinished)}
                    onDismiss={() => { forgetJob(orgId); setUnfinished(null); }} />
            )}
            <Notice notice={notice} onClose={() => setNotice(null)} />
            <ErrorLine text={foldersError} />
            <ErrorLine text={pageError} />

            <div className="flex flex-col md:flex-row gap-5">
                {/* folders: a column on wider screens, a fold-out on a phone */}
                <div className="md:w-56 md:shrink-0">
                    <button type="button" className={`${BTN_GHOST} w-full justify-between md:hidden`} aria-expanded={foldersOpenOnPhone} onClick={() => setFoldersOpenOnPhone((o) => !o)}>
                        <span className="truncate">Folders: {heading}</span>
                        {foldersOpenOnPhone ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <div className={`${foldersOpenOnPhone ? 'block mt-2' : 'hidden'} md:block`}>
                        <CatalogFolderTree tree={tree} selection={selection} onSelect={selectFolder} counts={counts}
                            expanded={expanded} onToggle={toggle} onCreate={createFolder} />
                    </div>
                </div>

                <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-2 min-h-[44px]">
                        {rename ? (
                            <div className="w-full space-y-2">
                                <label htmlFor="cat-rename" className="block text-sm font-medium text-gray-700">Rename folder</label>
                                <div className="flex flex-wrap gap-2">
                                    <input id="cat-rename" className={`${INPUT} flex-1 min-w-[12rem]`} autoFocus maxLength={100} value={rename.name}
                                        onChange={(e) => setRename((r) => ({ ...r, name: e.target.value, error: '' }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRename(null); }} />
                                    <button type="button" className={BTN_PRIMARY} disabled={rename.saving} onClick={saveRename}>{rename.saving ? 'Saving…' : 'Save'}</button>
                                    <button type="button" className={BTN_GHOST} disabled={rename.saving} onClick={() => setRename(null)}>Cancel</button>
                                </div>
                                <ErrorLine text={rename.error} />
                            </div>
                        ) : (
                            <>
                                <h3 className="text-base font-semibold text-gray-800 truncate">{heading}</h3>
                                {openFolder && (
                                    <>
                                        <button type="button" className={BTN_LINK} onClick={() => setRename({ name: openFolder.name, saving: false, error: '' })}>
                                            <Pencil size={14} /> Rename folder
                                        </button>
                                        <button type="button" className={BTN_LINK} onClick={() => setMoveFolder({ target: hereId, saving: false, error: '' })}>
                                            <FolderInput size={14} /> Move folder…
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <CatalogItemList search={search} query={query} onQuery={setQuery} type={type} onType={setType}
                        archivedView={selection.kind === 'archived'}
                        folderView={selection.kind === 'favorites' ? 'favorites' : (selection.kind === 'folder' || selection.kind === 'ungrouped')}
                        selectedId={editorTarget && editorTarget.mode === 'edit' ? editorTarget.id : null}
                        onOpen={(id) => requestOpen({ mode: 'edit', id })} folderNames={folderNames}
                        onNewItem={selection.kind === 'archived' ? undefined : newItemHere}
                        ticked={ticked} allMatching={allMatching} onTick={onTick} onTickShown={onTickShown}
                        onSelectAllMatching={() => setAllMatching(true)} onClearSelection={clearSelection}
                        onToggleFavorite={toggleFavorite} bulkBar={bulkBar} />

                    {/* phone: the editor opens under the list */}
                    <div ref={editorBoxRef}>
                        {editorOpen && !isWide && <div className="border border-gray-200 rounded-xl overflow-hidden">{editor}</div>}
                    </div>
                </div>
            </div>

            {/* wider screens: the editor is a side panel */}
            {editorOpen && isWide && (
                <>
                    <div className="fixed inset-0 z-40 bg-black/20" aria-hidden="true" onClick={() => requestOpen(null)} />
                    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl overflow-y-auto">{editor}</div>
                </>
            )}

            {bulkDialog === 'price' && (
                <PriceDialog open onClose={() => setBulkDialog(null)} previewRows={allMatching ? search.items : tickedRows}
                    allMatching={allMatching} count={selCount} exact={selExact} onStart={startBulk} />
            )}
            {(bulkDialog === 'moveToFolder' || bulkDialog === 'addToFolder') && (
                <FolderActionDialog open action={bulkDialog} onClose={() => setBulkDialog(null)} tree={tree} count={selCount} exact={selExact} onStart={startBulk} />
            )}
            {bulkDialog === 'archive' && <ArchiveDialog open onClose={() => setBulkDialog(null)} count={selCount} exact={selExact} onStart={startBulk} />}
            {exportSel && <ExportDialog open selection={exportSel.selection} label={exportSel.label} onClose={() => setExportSel(null)} />}
            <JobDialog runner={runner} />

            {moveFolder && openFolder && (
                <Dialog open title={`Move “${openFolder.name}”`} onClose={() => setMoveFolder(null)} busy={moveFolder.saving}
                    footer={(
                        <>
                            <button type="button" className={BTN_GHOST} disabled={moveFolder.saving} onClick={() => setMoveFolder(null)}>Cancel</button>
                            <button type="button" className={BTN_PRIMARY} disabled={moveFolder.saving || !moveFolder.target || moveFolder.target === hereId} onClick={saveMoveFolder}>
                                {moveFolder.saving ? 'Moving…' : 'Move folder'}
                            </button>
                        </>
                    )}>
                    <p className="text-sm text-gray-600">Choose where “{openFolder.name}” should go. Its sub-folders and items move with it.</p>
                    <CatalogFolderPicker tree={tree} value={moveFolder.target} onChange={(t) => setMoveFolder((m) => ({ ...m, target: t, error: '' }))}
                        exclude={folderAndBelow(tree, openFolder.id)} topLevel hereId={hereId} name="cat-move-folder" />
                    <ErrorLine text={moveFolder.error} />
                </Dialog>
            )}

            <DiscardDialog open={pendingOpen !== undefined}
                itemName={null}
                onKeep={() => setPendingOpen(undefined)}
                onDiscard={() => { const t = pendingOpen; setPendingOpen(undefined); openNow(t); }} />
        </div>
    );
}
